import axios from 'axios';
import type { HealthConfig, HealthTier, OrgHealthScore } from '../types';

/**
 * Helpers for the health-score settings dialog (PEL-2852). The backend is the authority on
 * what is valid; `healthConfigProblems` mirrors its rules so Save can be disabled with a
 * reason instead of failing with a 422.
 */

/** The backend's fixed rolling window. Habit can't target more active days than this. */
export const HEALTH_WINDOW_DAYS = 28;

/** Mirrors the backend's cap on the Depth target (a typo guard, not a business rule). */
export const MAX_DEPTH_TARGET = 10_000;

// The backend uses 1e-6; this is the tolerance for the *rounded* weights the UI produces,
// which are always 2dp, so anything beyond half a unit in the last place is a real error.
const WEIGHT_SUM_TOLERANCE = 0.0005;

export const TIER_ORDER: HealthTier[] = ['healthy', 'watch', 'at_risk', 'dormant'];

/**
 * The reason a health API call failed, as the backend phrased it. FastAPI puts it in
 * `detail` (a string, or a list of validation errors for a 422); axios's own message is
 * only "Request failed with status code 500".
 */
export function apiErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail: unknown = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.map((d: { msg?: string }) => d.msg ?? String(d)).join('; ');
    }
  }
  return err instanceof Error ? err.message : String(err);
}

/** The PUT/preview body: the backend's snake_case field names, every field present. */
export function toHealthConfigBody(config: HealthConfig) {
  return {
    weights: {
      activation: config.weights.activation,
      habit: config.weights.habit,
      depth: config.weights.depth,
      reliability: config.weights.reliability,
    },
    targets: {
      activation_seat_ratio: config.targets.activationSeatRatio,
      habit_active_days: config.targets.habitActiveDays,
      depth_requests_per_user: config.targets.depthRequestsPerUser,
      reliability_max_failure_rate: config.targets.reliabilityMaxFailureRate,
    },
    tiers: {
      healthy_min: config.tiers.healthyMin,
      watch_min: config.tiers.watchMin,
    },
  };
}

export function weightSum(config: HealthConfig): number {
  const { activation, habit, depth, reliability } = config.weights;
  return activation + habit + depth + reliability;
}

export function weightsBalanced(config: HealthConfig): boolean {
  return Math.abs(weightSum(config) - 1) <= WEIGHT_SUM_TOLERANCE;
}

/** Human-readable reasons the backend would reject this config; empty when it's valid. */
export function healthConfigProblems(config: HealthConfig): string[] {
  const problems: string[] = [];
  if (!weightsBalanced(config)) {
    problems.push(`Weights total ${weightSum(config).toFixed(2)}; they must total exactly 1.00.`);
  }
  const { activationSeatRatio, habitActiveDays, depthRequestsPerUser, reliabilityMaxFailureRate } =
    config.targets;
  if (!(activationSeatRatio > 0 && activationSeatRatio <= 1)) {
    problems.push('The Activation target must be a share of seats above 0 and at most 1.');
  }
  if (!(habitActiveDays > 0 && habitActiveDays <= HEALTH_WINDOW_DAYS)) {
    problems.push(`The Habit target must be between 0 and ${HEALTH_WINDOW_DAYS} days.`);
  }
  if (!(depthRequestsPerUser > 0 && depthRequestsPerUser <= MAX_DEPTH_TARGET)) {
    problems.push(`The Depth target must be above 0 and at most ${MAX_DEPTH_TARGET.toLocaleString()}.`);
  }
  if (!(reliabilityMaxFailureRate > 0 && reliabilityMaxFailureRate <= 1)) {
    problems.push('The Reliability ceiling must be a failure rate above 0 and at most 1.');
  }
  const { healthyMin, watchMin } = config.tiers;
  if (!(watchMin >= 0 && healthyMin <= 100 && watchMin < healthyMin)) {
    problems.push('The Watch cutoff must be below the Healthy cutoff, both within 0–100.');
  }
  return problems;
}

export function sameHealthConfig(a: HealthConfig, b: HealthConfig): boolean {
  return JSON.stringify(toHealthConfigBody(a)) === JSON.stringify(toHealthConfigBody(b));
}

export function tierCounts(rows: OrgHealthScore[]): Record<HealthTier, number> {
  const counts: Record<HealthTier, number> = { healthy: 0, watch: 0, at_risk: 0, dormant: 0 };
  rows.forEach((row) => {
    counts[row.tier] += 1;
  });
  return counts;
}

/** How many orgs land in a different tier under `next` than under `current`. */
export function tierChanges(current: OrgHealthScore[], next: OrgHealthScore[]): number {
  const before = new Map(current.map((row) => [row.orgId, row.tier]));
  return next.filter((row) => before.has(row.orgId) && before.get(row.orgId) !== row.tier).length;
}
